export const GAME_TIMER_PREFIX = 'bigpicture_game_timer';
export const STAGE_TIMER_PREFIX = 'bigpicture_stage_timer';

export function gameTimerKey(userId, sessionId) {
  return `${GAME_TIMER_PREFIX}_${userId}_${sessionId}`;
}

export function stageTimerKey(userId, sessionId, stageNumber) {
  return `${STAGE_TIMER_PREFIX}_${userId}_${sessionId}_${stageNumber}`;
}

function readTimerStart(key) {
  try {
    const raw = sessionStorage.getItem(key);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeTimerStart(key, start) {
  try {
    sessionStorage.setItem(key, String(start));
  } catch {
    /* ignore */
  }
}

/** Session-wide timer start (continues across stages and refreshes). */
export function ensureGameTimerStart(userId, sessionId) {
  if (!userId || !sessionId) return null;
  const key = gameTimerKey(userId, sessionId);
  const existing = readTimerStart(key);
  if (existing) return existing;
  const start = Date.now();
  writeTimerStart(key, start);
  return start;
}

/** Per-stage timer start (continues on refresh for the same stage). */
export function ensureStageTimerStart(userId, sessionId, stageNumber) {
  if (!userId || !sessionId || stageNumber == null) return null;
  const key = stageTimerKey(userId, sessionId, stageNumber);
  const existing = readTimerStart(key);
  if (existing) return existing;
  const start = Date.now();
  writeTimerStart(key, start);
  return start;
}

export function getElapsedSeconds(startTime) {
  if (!startTime) return 0;
  return Math.max(0, Math.floor((Date.now() - startTime) / 1000));
}

export function formatGameTimer(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

/** Parse theme stage timer strings such as "05:00" or seconds. */
export function parseStageTimerSeconds(raw, fallback = 300) {
  const s = String(raw ?? '').trim();
  if (!s) return fallback;
  if (s.includes(':')) {
    const parts = s.split(':').map((p) => Math.floor(Number(p)) || 0);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  const n = Math.floor(Number(s));
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Elapsed seconds from a countdown budget and remaining seconds. */
export function countdownElapsedSeconds(totalSeconds, secondsLeft) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const left = Math.max(0, Math.ceil(Number(secondsLeft) || 0));
  return Math.min(total, Math.max(0, total - left));
}

/** Elapsed seconds from an absolute play deadline (call before clearing deadline). */
export function elapsedFromPlayDeadline(deadlineMs, totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const deadline = Number(deadlineMs) || 0;
  if (!deadline) return 0;
  const remainingSec = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  return Math.min(total, Math.max(0, total - remainingSec));
}

/** Prefer deadline-based elapsed when available; otherwise use live countdown state. */
export function resolveCountdownElapsedSeconds({ totalSeconds, secondsLeft, deadlineMs }) {
  if (deadlineMs) {
    return elapsedFromPlayDeadline(deadlineMs, totalSeconds);
  }
  return countdownElapsedSeconds(totalSeconds, secondsLeft);
}
